package com.essntl.features.request.ui.addeditrequest

import com.essntl.core.utils.utils.loadingmanager.LoadingManager
import com.essntl.core.utils.viewmodel.PaginationState
import com.essntl.core.utils.viewmodel.State
import com.essntl.core.utils.viewmodel.ViewModel
import com.essntl.core.utils.viewmodel.getData
import com.essntl.core.utils.viewmodel.getPaginationData
import com.essntl.features.area.domain.repository.AreaRepository
import com.essntl.features.client.domain.repository.ClientRepository
import com.essntl.features.request.domain.model.RequestModel
import com.essntl.features.request.domain.repository.RequestRepository
import com.essntl.features.travelagent.domain.repository.TravelAgentRepository
import com.essntl.toastmanager.ToastManager
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.update

class AddEditRequestViewModel(
    private val requestRepository: RequestRepository,
    private val areaRepository: AreaRepository,
    private val travelAgentRepository: TravelAgentRepository,
    private val clientRepository: ClientRepository,
    private val toastManager: ToastManager,
    override val scope: CoroutineScope,
    override val loadingManager: LoadingManager,
    private val state: MutableStateFlow<AddEditRequestState>,
) : ViewModel {

    init {
        getAllArea()
        getNextTravelAgentPage()
    }

    fun onEvent(event: AddEditRequestEvent) {
        when (event) {
            is AddEditRequestEvent.LoadMoreTravelAgent -> getNextTravelAgentPage()

            is AddEditRequestEvent.SearchTravelAgent -> {
                state.value = state.value.copy(
                    getAllTravelAgent = PaginationState(query = event.query),
                )
                getNextTravelAgentPage()
            }

            is AddEditRequestEvent.AddRequest -> addRequest()

            is AddEditRequestEvent.EditRequest -> editRequest()

            is AddEditRequestEvent.GetRequest -> getRequest(event)

            is AddEditRequestEvent.UpdateRequestFields -> updateRequestFields(event)

            is AddEditRequestEvent.ClearSuccessState -> clearSuccessState()

            is AddEditRequestEvent.ClearAddEditTravelAgentSuccessState -> clearAddEditTravelAgentSuccessState()

            is AddEditRequestEvent.ClearRequestState -> clearRequestState()

            is AddEditRequestEvent.AddClient -> addClient(event)

            is AddEditRequestEvent.EditClient -> editClient(event)

            is AddEditRequestEvent.RemoveClient -> removeClient(event)

            is AddEditRequestEvent.AddTravelAgent -> addTravelAgent(event)

            is AddEditRequestEvent.EditTravelAgent -> editTravelAgent(event)
        }
    }

    private fun updateRequestFields(event: AddEditRequestEvent.UpdateRequestFields) {
        state.value.request.value = event.request
        val newRequest = state.value.getRequest.data?.getChangedFields(state.value.request.value) ?: return
        state.update {
            it.copy(isEditEnabled = !newRequest.isEmpty)
        }
    }

    private fun addRequest() = getData(
        getData = { requestRepository.insert(state.value.request.value) },
        getState = { state.value.addEditRequest },
        setState = { state.value = state.value.copy(addEditRequest = it) },
        onSuccess = { request ->
            state.update {
                it.copy(
                    getRequest = State.success(request),
                )
            }
            state.value.request.value = request

            toastManager.success(
                description = "Request created successfully",
            )
        },
    )

    private fun editRequest() {
        val newRequest = state.value.getRequest.data?.getChangedFields(state.value.request.value) ?: return
        val travelAgent = state.value.request.value.travelAgent

        if (newRequest.isEmpty) return

        getData(
            getData = { requestRepository.update(newRequest.copy(travelAgent = travelAgent)) },
            getState = { state.value.addEditRequest },
            setState = { state.value = state.value.copy(addEditRequest = it) },
            onSuccess = { request ->
                state.update {
                    it.copy(
                        getRequest = State.success(request),
                    )
                }
                state.value.request.value = request

                toastManager.success(
                    description = "Request updated successfully",
                )
            },
        )
    }

    private var getRequestJob: Job? = null
    private fun getRequest(event: AddEditRequestEvent.GetRequest) {
        if (state.value.getRequest.data?.id == event.requestId) return

        state.value.request.value = RequestModel.empty.copy(id = event.requestId)

        // Make sure to cancel the previous job
        // The user can switch between requests quickly before the job is finished,
        // In that case we don't want to show the result of the previous job
        getRequestJob?.cancel()
        getRequestJob = getData(
            getData = { requestRepository.getById(event.requestId) },
            getState = { state.value.getRequest },
            setState = {
                state.value = state.value.copy(
                    getRequest = it, isEditEnabled = false,
                )
            },
            onSuccess = { request ->
                state.value.request.value = request

                val clients = request.clients
                if (!clients.isNullOrEmpty()) getAllClient(clients)
            },
        )
    }

    private fun getAllArea() = getData(
        getData = { areaRepository.getAll() },
        getState = { state.value.getAllArea },
        setState = { state.value = state.value.copy(getAllArea = it) },
    )

    private fun getNextTravelAgentPage(query: String = state.value.getAllTravelAgent.query) = getPaginationData(
        getData = {
            val nextPage = state.value.getAllTravelAgent.loadedPages + 1L
            travelAgentRepository.getAll(page = nextPage, query = query)
        },
        getState = { state.value.getAllTravelAgent },
        setState = {
            state.value = state.value.copy(getAllTravelAgent = it)
        },
    )

    private fun getAllClient(ids: List<String>) = getData(
        getData = { clientRepository.getByIds(ids) },
        getState = { state.value.getAllClient },
        setState = { state.value = state.value.copy(getAllClient = it) },
    )

    private fun addClient(event: AddEditRequestEvent.AddClient) {
        state.update {
            it.copy(
                getAllClient = it.getAllClient.copy(
                    data = it.getAllClient.data.orEmpty() + event.client,
                ),
            )
        }

        state.value.request.value = state.value.request.value.copy(
            clients = state.value.request.value.clients.orEmpty() + event.client.id,
        )
    }

    private fun editClient(event: AddEditRequestEvent.EditClient) {
        state.update {
            it.copy(
                getAllClient = it.getAllClient.copy(
                    data = it.getAllClient.data.orEmpty().map { c ->
                        if (c.id == event.client.id) event.client else c
                    },
                ),
            )
        }
    }

    private fun removeClient(event: AddEditRequestEvent.RemoveClient) = getData(
        getData = { clientRepository.delete(event.clientId) },
        getState = {
            if (event.clientId in state.value.loadingRemoveClient) State.loading()
            else State()
        },
        setState = {
            val newLoadingRemoveClient = if (it.isLoading) state.value.loadingRemoveClient + event.clientId
            else state.value.loadingRemoveClient - event.clientId

            state.value = state.value.copy(
                loadingRemoveClient = newLoadingRemoveClient,
            )
        },
        onSuccess = {
            state.update {
                it.copy(
                    getAllClient = it.getAllClient.copy(
                        data = it.getAllClient.data.orEmpty().filter { c ->
                            c.id != event.clientId
                        },
                    ),
                )
            }

            state.value.request.value = state.value.request.value.copy(
                clients = state.value.request.value.clients.orEmpty().filter { c ->
                    c != event.clientId
                },
            )

            toastManager.success(
                description = "Client removed successfully",
            )
        },
    )

    private fun addTravelAgent(event: AddEditRequestEvent.AddTravelAgent) =
        state.update {
            it.copy(
                getAllTravelAgents = it.getAllTravelAgents.copy(
                    data = it.getAllTravelAgents.data.orEmpty() + event.travelAgent,
                ),
            )
        }.also {
            state.value.request.value = state.value.request.value.copy(
                travelAgent = event.travelAgent,
            )

            state.update {
                it.copy(
                    getRequest = it.getRequest.copy(
                        data = state.value.request.value,
                    ),
                )
            }
        }

    private fun editTravelAgent(event: AddEditRequestEvent.EditTravelAgent) =
        getData(
            getData = { travelAgentRepository.update(event.travelAgent) },
            getState = { state.value.addEditTravelAgent },
            setState = { state.value = state.value.copy(addEditTravelAgent = it) },
            onSuccess = { travelAgent ->
                state.update {
                    it.copy(
                        getAllTravelAgents = it.getAllTravelAgents.copy(
                            data = it.getAllTravelAgents.data.orEmpty().map { ta ->
                                if (ta.id == travelAgent.id) travelAgent else ta
                            },
                        ),
                    )
                }.also {
                    state.value.request.value = state.value.request.value.copy(
                        travelAgent = travelAgent,
                    )
                }

                toastManager.success(
                    description = "Travel agent updated successfully",
                )
            },
        )

    private fun clearSuccessState() {
        state.value = state.value.copy(
            addEditRequest = State(),
            isEditEnabled = false,
        )
    }

    private fun clearAddEditTravelAgentSuccessState() {
        state.value = state.value.copy(
            addEditTravelAgent = State(),
        )
    }

    private fun clearRequestState() {
        state.value = state.value.copy(
            getRequest = State(),
            addEditRequest = State(),
            getAllTravelAgents = State(),
            isEditEnabled = false,
        )

        state.value.request.value = RequestModel.empty.copy(
            status = RequestModel.Status.Todo,
        )
    }
}